import { Link } from 'react-router-dom';
import {
  DEPOSIT_STATE_LABELS,
  depositAmountCents,
  formatCents,
  type DepositState,
  type DepositType,
  type Estimate,
} from '@fsm/shared';
import { Badge, Card, Field, Input, Select } from './ui';

/**
 * The deposit on an estimate: the rule, what it comes to, and where it got to.
 *
 * ## Why the figure is shown and not just the rule
 *
 * "50%" is not a number anybody can check. The customer is going to be asked
 * for a sum of money, and the person setting the rule should see that sum
 * before the estimate goes out — on the same screen, updating as they type,
 * rather than discovering it on the invoice the customer queries.
 *
 * ## Why it stops being editable
 *
 * Once approval has raised the deposit invoice, the rule is history: the
 * customer has been sent a figure and may already have paid it. The card then
 * shows where that invoice is instead of pretending the number is still a
 * choice. The server enforces the same thing, so a stale tab cannot get round
 * it.
 */

const TONE: Record<DepositState, 'slate' | 'amber' | 'blue' | 'emerald'> = {
  not_required: 'slate',
  unpaid: 'amber',
  partial: 'blue',
  paid: 'emerald',
};

export interface DepositDraft {
  type: DepositType;
  valueCents: number;
  valueBps: number;
}

export function DepositCard({
  estimate,
  editing,
  draft,
  onChange,
}: {
  estimate: Estimate;
  /** Editing is the caller's decision; this card only reflects it. */
  editing: boolean;
  draft: DepositDraft;
  onChange: (next: DepositDraft) => void;
}) {
  const deposit = estimate.deposit;
  const invoiced = Boolean(deposit?.invoiceId);
  const canEdit = editing && !invoiced;

  const rule: DepositDraft = canEdit
    ? draft
    : {
        type: estimate.depositType,
        valueCents: estimate.depositValueCents,
        valueBps: estimate.depositValueBps,
      };

  /*
   * Recomputed here rather than read from the server's `deposit.amountCents`
   * while editing — the point of the live figure is that it answers for what
   * is on screen now, including an edit that has not been saved.
   */
  const totalCents = estimate.totals?.totalCents ?? 0;
  const amountCents = depositAmountCents(totalCents, {
    depositType: rule.type,
    depositValueCents: rule.valueCents,
    depositValueBps: rule.valueBps,
  });

  const state: DepositState = deposit?.state ?? (rule.type === 'none' ? 'not_required' : 'unpaid');

  return (
    <Card
      title="Deposit"
      icon="card"
      action={<Badge tone={TONE[state]}>{DEPOSIT_STATE_LABELS[state]}</Badge>}
    >
      {canEdit ? (
        <div className="space-y-3">
          <Field label="Taken on approval">
            <Select
              value={rule.type}
              onChange={(e) =>
                onChange({ ...rule, type: e.currentTarget.value as DepositType })
              }
            >
              <option value="none">No deposit</option>
              <option value="percent">A percentage of the total</option>
              <option value="fixed">A fixed amount</option>
            </Select>
          </Field>

          {rule.type === 'percent' && (
            <Field label="Percentage" hint="of the estimate total">
              <Input
                type="number"
                min={0}
                max={100}
                step={1}
                value={rule.valueBps / 100}
                onChange={(e) =>
                  onChange({
                    ...rule,
                    valueBps: Math.round(Number(e.currentTarget.value || 0) * 100),
                  })
                }
              />
            </Field>
          )}

          {rule.type === 'fixed' && (
            <Field label="Amount" hint="dollars">
              <Input
                type="number"
                min={0}
                step={0.01}
                value={(rule.valueCents / 100).toFixed(2)}
                onChange={(e) =>
                  onChange({
                    ...rule,
                    valueCents: Math.round(Number(e.currentTarget.value || 0) * 100),
                  })
                }
              />
            </Field>
          )}

          {rule.type !== 'none' && (
            <p className="text-[13px] text-ink-3">
              The customer will be invoiced{' '}
              <span className="font-semibold text-ink tnum">{formatCents(amountCents)}</span> when
              they approve this estimate.
            </p>
          )}
        </div>
      ) : (
        <Summary
          state={state}
          amountCents={deposit?.amountCents ?? amountCents}
          paidCents={deposit?.paidCents ?? null}
          invoiceId={deposit?.invoiceId ?? null}
          invoiceNumber={deposit?.invoiceNumber ?? null}
        />
      )}
    </Card>
  );
}

function Summary({
  state,
  amountCents,
  paidCents,
  invoiceId,
  invoiceNumber,
}: {
  state: DepositState;
  amountCents: number;
  paidCents: number | null;
  invoiceId: string | null;
  invoiceNumber: number | null;
}) {
  if (state === 'not_required') {
    return (
      <p className="text-[13px] text-ink-3">
        Nothing is taken up front. The whole job is billed when the work is done.
      </p>
    );
  }

  return (
    <div className="space-y-2 text-[13px]">
      <Row label="Deposit" value={formatCents(amountCents)} />
      {/*
        `paidCents` is null when the screen did not load the deposit invoice.
        Showing "$0.00 paid" in that case would be a claim nobody checked.
      */}
      {paidCents !== null && <Row label="Paid" value={formatCents(paidCents)} />}

      {invoiceId ? (
        <Link
          to={`/invoices/${invoiceId}`}
          className="inline-block pt-1 font-semibold text-accent hover:underline"
        >
          Deposit invoice #{invoiceNumber ?? '—'} →
        </Link>
      ) : (
        <p className="pt-1 text-ink-4">
          The invoice is raised when the customer approves this estimate.
        </p>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className="text-ink-3">{label}</span>
      <span className="tnum font-semibold text-ink">{value}</span>
    </div>
  );
}
